import { supabase } from '../services/supabase';

/**
 * A random, first-party id for this browser, sent with sponsor-ad clicks so the
 * per-creator click caps can't be reset by a browser update or a rotating IPv6
 * address (27 Oct 2026). It's opaque (no personal data) and the server stores
 * only a salted hash of it. If storage is unavailable (private mode, blocked
 * storage) there's simply no id and the server falls back to its network caps.
 */
export const AD_DEVICE_STORAGE_KEY = 'so_ad_device_id';
const DEVICE_ID_PATTERN = /^[A-Za-z0-9-]{16,64}$/;

function randomId(): string {
  const c: Crypto | undefined = typeof crypto !== 'undefined' ? crypto : undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function getAdDeviceId(): string | null {
  try {
    const saved = window.localStorage.getItem(AD_DEVICE_STORAGE_KEY);
    if (saved && DEVICE_ID_PATTERN.test(saved)) return saved;
    const fresh = randomId();
    window.localStorage.setItem(AD_DEVICE_STORAGE_KEY, fresh);
    // Only report an id that actually persisted, so it's stable across visits.
    return window.localStorage.getItem(AD_DEVICE_STORAGE_KEY) === fresh ? fresh : null;
  } catch {
    return null;
  }
}

const OWNER_BIND_SESSION_KEY = 'so_ad_owner_bound';

/**
 * When the signed-in visitor owns this page, tell the server this browser is
 * the owner's, so its taps on the page's sponsor cards never bill (even after
 * signing out). The server checks ownership itself; this runs at most once per
 * page per browser session.
 */
export async function bindOwnerAdDevice(talentId: string | null | undefined, ownerUserId: string | null | undefined): Promise<boolean> {
  if (!talentId || !ownerUserId) return false;
  try {
    const marker = `${OWNER_BIND_SESSION_KEY}:${talentId}`;
    if (window.sessionStorage.getItem(marker) === '1') return false;
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id || session.user.id !== ownerUserId) return false;
    const deviceId = getAdDeviceId();
    if (!deviceId) return false;
    const { data, error } = await supabase.rpc('bind_owner_click_device', { p_talent_id: talentId, p_device_id: deviceId });
    if (error || !data?.ok) return false;
    window.sessionStorage.setItem(marker, '1');
    return true;
  } catch {
    return false;
  }
}

/**
 * Bind now, and again when someone signs in on this page (the owner may open
 * it signed out and sign in through the account modal). Returns the cleanup.
 */
export function watchOwnerAdDevice(talentId: string | null | undefined, ownerUserId: string | null | undefined): () => void {
  if (!talentId || !ownerUserId) return () => {};
  void bindOwnerAdDevice(talentId, ownerUserId);
  try {
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (event !== 'SIGNED_IN' || session?.user?.id !== ownerUserId) return;
      // Supabase advises against awaiting its own calls inside this callback.
      setTimeout(() => { void bindOwnerAdDevice(talentId, ownerUserId); }, 0);
    });
    return () => data.subscription.unsubscribe();
  } catch {
    return () => {};
  }
}
